import React, { Fragment, useRef } from "react";
import { Formik } from "formik";
import { useCtxReferences, useProduct } from "utils/selectors";
import { UPDATE_PRODUCT_SCHEMA } from "constants/validation";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import isEmpty from "lodash/isEmpty";
import HoverIconButton from "components/HoverIconButton";
import Link from "next/link";
import {
  updateMerchantAccountProducts,
  archiveMerchantAccountProducts
} from "actions/product";
import { fetchShortLinks } from "actions/shortLink";
import { fetchIntegrations } from "actions/integration";
import {
  getUpdateProductParams,
  prepareEditProductInitialValues
} from "utils/product";
import { usePageBuilderContext } from "utils/context";
import Analytics, { UPDATE_PRODUCT } from "utils/analytics";
import get from "lodash/get";
import DetailsFields from "components/ProductForm/Fields/Details";
import TaxFields from "components/ProductForm/Fields/Tax";
import ActionHeaderFields from "components/ProductForm/Fields/ActionHeader";
import { usePrevPathFragment } from "utils/hooks/hub";
import { getProductArchiveModalSlides } from "utils/constants/modal";
import ActiveField from "components/ActiveField";
import ProductPricesSection from "components/ProductForm/ProductPricesSection";
import ProductLinksSection from "components/ProductForm/ProductLinksSection";
import ProductIntegrations from "components/ProductForm/ProductIntegrations";
import MetadataSection from "components/MetadataSection";
import {
  ensureIntegrations,
  ensureMerchantAccounts,
  ensureProductsPrices,
  ensureShortLinks,
  useModelQueryId,
  useScrollTopOnMount
} from "utils/hooks";
import { PRODUCT } from "utils/constants/models";
import SectionBezel from "components/SectionBezel";
import {
  fetchMerchantAccounts,
  fetchMerchantAccountProductsPrices
} from "actions/merchantAccount";
import { useRouter } from "next/router";
import ConnectedField from "components/ConnectedField";
import {
  BUTTON_TAGS,
  MAIN_CONTENT_MAX_WIDTH,
  NAV_BAR_HEIGHT
} from "utils/constants/ui";
import CardDetailLoader from "components/CardDetailLoader";
import SlimCTA from "components/SlimCTA";
import PresentLayout from "components/PresentLayout";
import ImagesFields from "components/ProductForm/Fields/Images";
import { handleFormCb } from "utils/form";
import { useStripeFeature } from "utils/hooks/stripe";
import { STRIPE_FEATURES } from "utils/constants/stripe";
import { isPlatformSubdomain } from "utils/subdomain";
import { getAccountConnectStatusProps } from "utils/merchantAccount";
import StripeExternalLink from "components/StripeExternalLink";
import { getStripeDestination } from "utils/stripe";

const DEFAULT_BACK_ACTION = {
  icon: "back",
  tooltip: "All products",
  copy: "Back"
};

const EditProduct = ({ actions, backAction = DEFAULT_BACK_ACTION }) => {
  const backActionProps = { ...DEFAULT_BACK_ACTION, ...backAction };
  const backPath = usePrevPathFragment();
  const id = useModelQueryId(PRODUCT);
  useScrollTopOnMount();

  const taxEnabled = useStripeFeature({ feature: STRIPE_FEATURES.TAX });

  const { replace } = useRouter();
  const product = useProduct(id);
  const {
    resourceUUID,
    resource,
    merchantAccountUUID,
    merchantAccount
  } = useCtxReferences();

  const { application } = useCtxReferences();
  const isPlatform = isPlatformSubdomain(application && application.subdomain);
  const account =
    merchantAccount && getAccountConnectStatusProps(merchantAccount);
  /**
   * Fetch records
   */
  ensureShortLinks({ fetchShortLinks: actions.fetchShortLinks });
  ensureIntegrations({ fetchIntegrations: actions.fetchIntegrations });
  const { loading } = ensureProductsPrices({
    fetchMerchantAccountProductsPrices:
      actions.fetchMerchantAccountProductsPrices
  });
  ensureMerchantAccounts({
    fetchMerchantAccounts: actions.fetchMerchantAccounts
  });

  const context = usePageBuilderContext();
  const formRef = useRef(null);

  const goBack = () => {
    replace(backPath);
  };

  const updateProduct = (formValues, formikActions) => {
    Analytics.track(UPDATE_PRODUCT, {
      ...get(context, "tracking", {})
    });
    const updatedProduct = getUpdateProductParams(
      formRef.current.initialValues,
      formValues
    );

    if (!isEmpty(updatedProduct)) {
      const data = {
        products: [
          {
            id: product.id,
            ...updatedProduct
          }
        ]
      };

      actions.updateMerchantAccountProducts(
        {
          resourceUUID,
          resource,
          uuid: merchantAccountUUID,
          data
        },
        (err) => handleFormCb(formValues, err, formikActions)
      );
    } else {
      formikActions.setSubmitting(false);
      formikActions.resetForm({ values: formValues });
    }
  };

  const archiveProduct = (callback) => {
    const deleteDataInput = {
      products: [
        {
          id: product.id
        }
      ]
    };
    actions.archiveMerchantAccountProducts(
      {
        resourceUUID,
        resource,
        uuid: merchantAccountUUID,
        data: deleteDataInput
      },
      (err, products) => {
        if (!err) {
          goBack();
        }
        callback(err, products);
      }
    );
  };

  if (loading) {
    return (
      <div className={`${MAIN_CONTENT_MAX_WIDTH} pa3`}>
        <CardDetailLoader />
      </div>
    );
  } else if (!product) {
    return (
      <div className={`${MAIN_CONTENT_MAX_WIDTH} pa3`}>
        <SlimCTA
          header="No product found"
          subheader="We can't find the product you're looking for."
          actions={[
            {
              copy: "Back",
              tag: BUTTON_TAGS.LINK,
              href: backPath
            }
          ]}
        />
      </div>
    );
  } else {
    return (
      <Fragment>
        {product ? (
          <Formik
            innerRef={formRef}
            initialValues={prepareEditProductInitialValues(product)}
            onSubmit={updateProduct}
            validate={async (values) =>
              UPDATE_PRODUCT_SCHEMA.validate(values)
                .then(() => ({}))
                .catch((err) => ({
                  [err.path]: err.message
                }))
            }
          >
            {({ values }) => {
              const nodes = [
                <DetailsFields />,
                <ProductPricesSection />,
                <ProductLinksSection />
              ];
              if (taxEnabled) {
                nodes.splice(1, 0, <TaxFields />);
              }

              if (isPlatform) {
                nodes.push(
                  <SectionBezel header="Integrations">
                    <ProductIntegrations
                      uid={values.id}
                      customClasses={{
                        container: "flex flex-column w-100"
                      }}
                    />
                  </SectionBezel>
                );
              }

              nodes.push(<MetadataSection name="metadata" />);
              nodes.push(
                <SectionBezel header="Billing">
                  <ConnectedField
                    name="statement_descriptor"
                    label="Statement descriptor"
                  />
                </SectionBezel>
              );
              const presentProps = {};

              presentProps.banner = (
                <div className="pt3 relative w-100 mt5-l center">
                  <ImagesFields />
                </div>
              );

              presentProps.back = (
                <Link href={backPath}>
                  <div className="flex flex-row items-center pointer">
                    <HoverIconButton
                      type={backActionProps.icon}
                      tooltip={backActionProps.tooltip}
                    />
                    <div className="pl1 f6 fw5 pointer">
                      {backActionProps.copy}
                    </div>
                  </div>
                </Link>
              );

              presentProps.header = (
                <ActionHeaderFields
                  actions={[
                    {
                      slides: getProductArchiveModalSlides(values),
                      icon: "archive",
                      tooltip: "Archive",
                      onConfirm: archiveProduct,
                      children: (
                        <div className="fl pv3 relative z-1">
                          <StripeExternalLink
                            account={account}
                            copy={`Manage on Stripe`}
                            href={getStripeDestination({
                              path: `products/${product.id}`
                            })}
                          />
                        </div>
                      )
                    }
                  ]}
                >
                  <ActiveField
                    name="active"
                    value={values.active}
                    customClasses={{
                      container:
                        "w-100 flex flex-row justify-between items-center",
                      toggle: "flex flex-shrink-0 pl2",
                      label: "f6 fw5 w-100"
                    }}
                  />
                </ActionHeaderFields>
              );

              presentProps.content = nodes.map((node, nodeIx) => (
                <div className="pt3 pt4-ns" key={nodeIx}>
                  {node}
                </div>
              ));

              return (
                <PresentLayout {...presentProps} mobileTop={NAV_BAR_HEIGHT} />
              );
            }}
          </Formik>
        ) : null}
      </Fragment>
    );
  }
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      updateMerchantAccountProducts,
      archiveMerchantAccountProducts,
      fetchMerchantAccountProductsPrices,
      fetchMerchantAccounts,
      fetchIntegrations,
      fetchShortLinks
    },
    dispatch
  )
});

export default connect(null, mapDispatchToProps)(EditProduct);
